import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, SERVICE_KEYS } from "./i18n";

const field = "w-full h-12 rounded-2xl border border-[#0A1128]/12 bg-white px-4 text-base text-[#0A1128] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#E8173A]/40 focus:border-[#E8173A] transition-[box-shadow,border-color]";
const label = "block text-xs font-bold uppercase tracking-[0.14em] text-[#0A1128]/70 mb-2";
const EMPTY = { nome: "", azienda: "", email: "", telefono: "", servizi: [], messaggio: "", privacy: false, website: "" };

export const ContactForm = () => {
  const { t, lang } = useLang();
  const c = t.contatti;
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => setF({ ...f, servizi: f.servizi.includes(k) ? f.servizi.filter((x) => x !== k) : [...f.servizi, k] });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.privacy) { setErr(c.needPrivacy); return; }
    setErr(""); setState("sending");
    try { await api.post("/public/contatti", { ...f, lingua: lang }); setState("ok"); setF(EMPTY); }
    catch (ex) { setState("idle"); setErr(ex?.response?.status === 429 ? ex.response.data.detail : c.err); }
  };
  if (state === "ok") {
    return (
      <div className="rounded-[28px] bg-white p-8 sm:p-10 text-center shadow-[0_18px_50px_rgba(10,17,40,0.10)]" data-testid="contact-success">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#E8173A] text-white flex items-center justify-center"><Check className="h-8 w-8" /></div>
        <h3 className="mt-5 font-display text-2xl font-black">{c.ok}</h3>
        <p className="mt-2 text-slate-600">{c.okSub}</p>
        <button onClick={() => setState("idle")} className="mt-6 h-11 px-5 rounded-full border border-[#0A1128]/15 font-bold text-sm" data-testid="contact-again">OK</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-[28px] bg-white p-6 sm:p-10 shadow-[0_18px_50px_rgba(10,17,40,0.10)] grid gap-5" data-testid="contact-form" noValidate={false}>
      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid="contact-hp" />
      <div className="grid gap-5 sm:grid-cols-2">
        <div><label className={label} htmlFor="cf-nome">{c.nome} *</label><input id="cf-nome" required minLength={2} value={f.nome} onChange={set("nome")} className={field} autoComplete="name" data-testid="contact-nome" /></div>
        <div><label className={label} htmlFor="cf-azienda">{c.azienda}</label><input id="cf-azienda" value={f.azienda} onChange={set("azienda")} className={field} autoComplete="organization" data-testid="contact-azienda" /></div>
        <div><label className={label} htmlFor="cf-email">{c.email} *</label><input id="cf-email" type="email" required value={f.email} onChange={set("email")} className={field} autoComplete="email" data-testid="contact-email" /></div>
        <div><label className={label} htmlFor="cf-tel">{c.telefono}</label><input id="cf-tel" type="tel" value={f.telefono} onChange={set("telefono")} className={field} autoComplete="tel" data-testid="contact-telefono" /></div>
      </div>
      <div>
        <div className={label}>{c.servizi}</div>
        <div className="flex flex-wrap gap-2" data-testid="contact-servizi">
          {SERVICE_KEYS.map((k) => {
            const on = f.servizi.includes(k);
            return (
              <button type="button" key={k} onClick={() => toggle(k)} aria-pressed={on} data-testid={`contact-servizio-${k}`}
                className={`min-h-[40px] px-4 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-[#EAF6FF] text-[#0A1128] border-[#7CC6FA] hover:border-[#0A1128]"}`}>
                {on && <Check className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />}{t.servizi.items[k][0]}
              </button>
            );
          })}
        </div>
      </div>
      <div><label className={label} htmlFor="cf-msg">{c.messaggio} *</label><textarea id="cf-msg" required minLength={5} rows={5} value={f.messaggio} onChange={set("messaggio")} className={`${field} h-auto py-3 resize-y`} data-testid="contact-messaggio" /></div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-privacy-label">
        <input type="checkbox" checked={f.privacy} onChange={(e) => setF({ ...f, privacy: e.target.checked })} className="mt-0.5 h-5 w-5 shrink-0 accent-[#E8173A]" data-testid="contact-privacy" />
        <span className="text-sm text-slate-700">{c.privacy} <Link to="/privacy" className="font-bold text-[#E8173A] underline underline-offset-2">{t.footer.privacy}</Link></span>
      </label>
      <p className="text-xs text-slate-500 -mt-2">{c.privacyNote}</p>
      {err && <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm font-semibold text-[#C8102E]" data-testid="contact-error">{err}</div>}
      <button type="submit" disabled={state === "sending"} className="h-14 rounded-full bg-[#E8173A] hover:bg-[#C8102E] disabled:opacity-70 text-white font-bold text-base inline-flex items-center justify-center gap-2 transition-colors" data-testid="contact-submit">
        {state === "sending" ? <><Loader2 className="h-5 w-5 animate-spin" />{c.sending}</> : <><Send className="h-5 w-5" />{c.invia}</>}
      </button>
    </form>
  );
};
